import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import cartUtils from "~/utils/cart-utils";
import TrackingUtils from "~/utils/tracking-utils";

type CheckoutStep = "shipping" | "billing" | "payment";
type CheckoutFastForward = { shipping: boolean; billing: boolean };

const Context = createContext<{
  step: CheckoutStep;
  setStep: (value: CheckoutStep) => void;
  fiscalId?: string;
  setFiscalId: Dispatch<SetStateAction<string | undefined>>;
  globalLoading: boolean;
  setGlobalLoading: Dispatch<SetStateAction<boolean>>;
  addressStatus?: string;
  setAddressStatus: Dispatch<SetStateAction<"select" | "edit" | "new">>;
  stepFastForward: CheckoutFastForward;
  setStepFastForward: Dispatch<SetStateAction<CheckoutFastForward>>;
  onSuccessfulShippingSubmit: (method: string, isPrefilled?: boolean) => void;
  onSuccessfulBillingSubmit: (fiscalId: string) => void;
  cart: EVA.Core.ShoppingCartResponse | undefined;
  isCartLoading: boolean;
  productsParentsData?: Record<string, any>;
  orderSummaryOpen?: boolean;
  openOrderSummary: (value: boolean) => void;
  showStockDetails: boolean;
  openStockDetails: (value: boolean) => void;
  storeRef?: AbstractShop;
  setStoreRef: (store: AbstractShop) => void;
}>({
  step: "shipping",
  setStep: () => {},
  fiscalId: undefined,
  setFiscalId: () => {},
  globalLoading: false,
  setGlobalLoading: () => {},
  addressStatus: "select",
  setAddressStatus: () => {},
  stepFastForward: {
    shipping: false,
    billing: false,
  },
  setStepFastForward: () => {},
  onSuccessfulShippingSubmit: () => {},
  onSuccessfulBillingSubmit: () => {},
  cart: undefined,
  isCartLoading: true,
  orderSummaryOpen: false,
  openOrderSummary: () => {},
  showStockDetails: false,
  openStockDetails: () => {},
  setStoreRef: () => {},
});

export default function CartProvider(props: {
  children: React.ReactNode;
  cart: EVA.Core.ShoppingCartResponse;
  isCartLoading: boolean;
  productsParentsData?: Record<string, any>;
  storeHasStock?: boolean;
}) {
  const { cart, productsParentsData } = props;

  const [step, setStep] = useState<CheckoutStep>(() => {
    // if the cart has no delivery it has virtual product or has set store pickup as shipping method
    const noDelivery = !props.cart.ShoppingCart.HasDelivery;
    const onlyVirtualProducts = cartUtils.hasOnlyVirtualProducts(props.cart);
    if (noDelivery && (onlyVirtualProducts || props.storeHasStock)) {
      // Start from the billing step if the order has no delivery

      if (props.storeHasStock) {
        TrackingUtils.track(
          TrackingUtils.mapEcomCart("add_address_info", cart?.ShoppingCart, productsParentsData, {
            shipping_delivery: "collect_in_store",
            prefilled: "yes",
          })
        );
      }

      return "billing";
    }
    return "shipping";
  });
  const [addressStatus, setAddressStatus] = useState<"select" | "edit" | "new">("select");
  const [stepFastForward, setStepFastForward] = useState<CheckoutFastForward>({
    shipping: false,
    billing: false,
  });

  // The call to `Core.SetFiscalOrderData` requires the customer to exist.
  // This means that the fiscal id needs to be transported from the billing step
  // to the payment step.
  const [fiscalId, setFiscalId] = useState<string | undefined>();
  const [globalLoading, setGlobalLoading] = useState(false);

  const [orderSummaryOpen, setOrderSummaryOpen] = useState<boolean>(false);
  const openOrderSummary = (open: boolean) => {
    setOrderSummaryOpen(open);
  };
  const [showStockDetails, setShowStockDetails] = useState(false);
  const openStockDetails = (open: boolean) => {
    setShowStockDetails(open);
  };
  const [storeRef, setStoreRef] = useState<AbstractShop | undefined>();

  const onSuccessfulShippingSubmit = (method: string, isPrefilled?: boolean) => {
    setStep("billing");
    TrackingUtils.track(
      TrackingUtils.mapEcomCart("add_address_info", cart?.ShoppingCart, productsParentsData, {
        shipping_delivery: method,
        prefilled: isPrefilled ? "yes" : "no",
      })
    );
    if (!isPrefilled && document.getElementById("shipping")) {
      document.getElementById("shipping")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const onSuccessfulBillingSubmit = (fiscalId: string) => {
    setStep("payment");
    setFiscalId(fiscalId);
    const trackPayload: Record<string, any> = {};
    const firstProductLine = cart.ShoppingCart.Lines.find((l) => l.ProductID);
    const shippingTier = firstProductLine?.ShippingMethod?.Name;
    if (shippingTier) {
      trackPayload.shipping_tier = shippingTier;
    }
    TrackingUtils.track(
      TrackingUtils.mapEcomCart("add_shipping_info", cart?.ShoppingCart, productsParentsData, trackPayload)
    );
  };

  return (
    <Context.Provider
      value={{
        step,
        setStep,
        addressStatus,
        setAddressStatus,
        stepFastForward,
        setStepFastForward,
        onSuccessfulShippingSubmit,
        onSuccessfulBillingSubmit,
        fiscalId,
        setFiscalId,
        globalLoading,
        setGlobalLoading,
        cart: props.cart,
        isCartLoading: props.isCartLoading,
        productsParentsData: props.productsParentsData,
        orderSummaryOpen,
        openOrderSummary,
        showStockDetails,
        openStockDetails,
        storeRef,
        setStoreRef,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

export function useCartContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useCartContext must be used within a CartProvider`);
  }

  return context;
}

export function useGuaranteedCart() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useGuaranteedCart must be used within a CartProvider`);
  }

  if (!context.cart) {
    throw new Error(`useGuaranteedCart must be used when a cart is available`);
  }

  return context.cart!;
}

export function useGuaranteedCartWithParentsData() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useGuaranteedCart must be used within a CartProvider`);
  }

  if (!context.cart) {
    throw new Error(`useGuaranteedCart must be used when a cart is available`);
  }

  return { cart: context.cart!, cartParentsData: context.productsParentsData || {} };
}

export function useOrderSummaryContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(`useOrderSummaryContext must be used within a CartProvider`);
  }
  return {
    openOrderSummary: context.orderSummaryOpen,
    setOpenOrderSummary: context.openOrderSummary,
    showStockDetails: context.showStockDetails,
    openStockDetails: context.openStockDetails,
    storeRef: context.storeRef,
    setStoreRef: context.setStoreRef,
  };
}
